/**
 * 自适应API配置器 (简化版)
 * 统一使用 Nginx 代理路径 /api
 */

import axios from 'axios';
import {
  ensureFreshAccessToken,
  handleSessionExpired,
  isSessionExpiryResponse,
  refreshAccessToken
} from '@/utils/sessionExpiryHandler';

function isPublicPath(url = '') {
  const path = String(url || '').toLowerCase();
  return path.includes('/login') || path.includes('/auth/') || path.includes('/health');
}

function isAuthRetrySkipped(url = '') {
  const path = String(url || '').toLowerCase();
  return path.includes('/auth/login') || path.includes('/auth/refresh');
}

function createSilentAuthError() {
  const silentError = new Error('会话已过期');
  silentError._isAuthError = true;
  silentError._silent = true;
  return silentError;
}

class AdaptiveAPIConfig {
  constructor() {
    this.currentConfig = {
      environment: {
        type: 'production',
        name: '统一代理环境'
      },
      api: {
        baseURL: '/api',
        timeout: 60000,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    };
    this.axiosInstance = null;
    this.isInitialized = false;
  }

  async initialize() {
    this.createAxiosInstance();
    this.isInitialized = true;
    return this.currentConfig;
  }

  createAxiosInstance() {
    this.axiosInstance = axios.create(this.currentConfig.api);

    // 请求拦截器 - 自动添加 Authorization header + 无 token 前置拦截
    this.axiosInstance.interceptors.request.use(
      async config => {
        config.headers = config.headers || {};
        const token = isPublicPath(config.url)
          ? localStorage.getItem('token')
          : await ensureFreshAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (!isPublicPath(config.url)) {
          // 无 token 时直接拦截请求，不发到后端，避免 401 报错暴露端点
          const refreshed = await refreshAccessToken();
          if (refreshed?.token) {
            config.headers.Authorization = `Bearer ${refreshed.token}`;
          } else {
            return Promise.reject(createSilentAuthError());
          }
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        // 401 静默处理：不 console.error，避免暴露 API 端点
        if (error.response && error.response.status === 401) {
          const originalConfig = error.config || {};
          if (!originalConfig._authRetry && !isAuthRetrySkipped(originalConfig.url)) {
            originalConfig._authRetry = true;
            const refreshed = await refreshAccessToken();
            if (refreshed?.token) {
              originalConfig.headers = originalConfig.headers || {};
              originalConfig.headers.Authorization = `Bearer ${refreshed.token}`;
              return this.axiosInstance(originalConfig);
            }
          }
          if (isSessionExpiryResponse(error) && !originalConfig._skipSessionExpiryPrompt) {
            await handleSessionExpired();
          }
          return Promise.reject(createSilentAuthError());
        }
        return Promise.reject(error);
      }
    );
  }

  getAxiosInstance() {
    if (!this.axiosInstance) {
      this.createAxiosInstance();
    }
    return this.axiosInstance;
  }

  getCurrentConfig() {
    return this.currentConfig;
  }
}

const adaptiveAPI = new AdaptiveAPIConfig();
export default adaptiveAPI;
